import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TIER_CATALOG, TRIAL_DAYS, ANNUAL_SAVINGS_PERCENT, SALES_EMAIL, formatCAD,
} from '@/lib/billing';
import { getCatalog, type SubscriptionPlan } from '@/lib/catalog';
import type { SubscriptionTier } from '@/lib/types';
import { PricingComparison } from '@/components/PricingComparison';
import { BrandedLayout } from '@/components/BrandedLayout';

/**
 * /pricing — public marketing page. Reads catalog from DB (subscription_plans
 * table); falls back to TIER_CATALOG seed if DB is unreachable.
 */
export default function Pricing() {
  const [period, setPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    getCatalog()
      .then((p) => { if (!cancelled && p.length > 0) setPlans(p); })
      .catch(() => { /* fall back to TIER_CATALOG via planFor() */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <BrandedLayout>
    <div className="container mx-auto py-10 px-4">
      <div className="max-w-5xl mx-auto space-y-12">
        <header className="space-y-3 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">
            <span style={{ color: 'hsl(var(--brand-navy))' }}>Pharma</span>
            <span style={{ color: 'hsl(var(--brand-teal))' }}>Sync</span>
            {' '}pricing
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Per-location pricing. {TRIAL_DAYS}-day free trial — no credit card.
            Designed for independent Canadian pharmacies, hosted in Canada.
          </p>
          <BillingPeriodToggle value={period} onChange={setPeriod} />
        </header>

        <section className="grid gap-6 md:grid-cols-3" aria-label="Pricing tiers">
          <TierCard tier="standard" period={period} plans={plans} highlight={false} />
          <TierCard tier="pro"      period={period} plans={plans} highlight={true} />
          <TierCard tier="enterprise" period={period} plans={plans} highlight={false} />
        </section>

        <section aria-labelledby="compare-heading" className="space-y-4">
          <div className="space-y-1">
            <h2 id="compare-heading" className="text-xl font-semibold">Compare plans side by side</h2>
            <p className="text-sm text-muted-foreground">
              Showing {period === 'annual' ? 'annual (per-month equivalent)' : 'monthly'} pricing.
              Toggle above to switch.
            </p>
          </div>
          <PricingComparison plans={plans} period={period} variant="public" />
        </section>

        <FAQ />
      </div>
    </div>
    </BrandedLayout>
  );
}

function planFor(tier: SubscriptionTier, plans: SubscriptionPlan[]) {
  const fromDb = plans.find((p) => p.tier === tier);
  if (fromDb) return {
    name: fromDb.name, blurb: fromDb.blurb,
    monthlyCAD: fromDb.monthly_cad, annualCAD: fromDb.annual_cad,
    cta: fromDb.cta, limits: fromDb.limits_blurb ?? '',
    features: TIER_CATALOG[tier].features, // features still in code
  };
  return {
    ...TIER_CATALOG[tier],
    limits: TIER_CATALOG[tier].limits,
  };
}

function BillingPeriodToggle({
  value, onChange,
}: { value: 'monthly' | 'annual'; onChange: (v: 'monthly' | 'annual') => void }) {
  return (
    <div role="tablist" aria-label="Billing period"
      className="inline-flex items-center rounded-full border border-border p-1 bg-muted/40">
      <button role="tab" aria-selected={value === 'monthly'} onClick={() => onChange('monthly')}
        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
          value === 'monthly' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
        }`}>
        Monthly
      </button>
      <button role="tab" aria-selected={value === 'annual'} onClick={() => onChange('annual')}
        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
          value === 'annual' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
        }`}>
        Annual
        <span className="ml-2 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={{ backgroundColor: 'hsl(var(--brand-teal) / 0.18)', color: 'hsl(var(--brand-navy))' }}>
          save {ANNUAL_SAVINGS_PERCENT}%
        </span>
      </button>
    </div>
  );
}

function TierCard({
  tier, period, plans, highlight,
}: {
  tier: SubscriptionTier; period: 'monthly' | 'annual';
  plans: SubscriptionPlan[]; highlight: boolean;
}) {
  const t = planFor(tier, plans);
  const price = period === 'annual' ? t.annualCAD : t.monthlyCAD;

  return (
    <article
      className={`rounded-lg border bg-card p-6 flex flex-col gap-4 ${
        highlight ? 'border-2 ring-1 ring-offset-0' : 'border-border'
      }`}
      style={highlight ? { borderColor: 'hsl(var(--brand-navy))', boxShadow: '0 1px 0 hsl(var(--brand-navy) / 0.08)' } : undefined}
      aria-labelledby={`tier-${tier}-name`}>
      {highlight && (
        <span className="self-start rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={{ backgroundColor: 'hsl(var(--brand-navy))', color: 'white' }}>
          Most flexible
        </span>
      )}
      <h2 id={`tier-${tier}-name`} className="text-xl font-semibold" style={{ color: 'hsl(var(--brand-navy))' }}>
        {t.name}
      </h2>
      <p className="text-sm text-muted-foreground">{t.blurb}</p>

      <div className="space-y-1">
        {price === null ? (
          <div className="text-2xl font-semibold">Talk to us</div>
        ) : (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-semibold">{formatCAD(price)}</span>
              <span className="text-sm text-muted-foreground">/ location / month</span>
            </div>
            {period === 'annual' && (
              <p className="text-xs text-muted-foreground">
                Billed annually ({formatCAD(price * 12)} per location).
              </p>
            )}
            {period === 'monthly' && t.annualCAD !== null && (
              <p className="text-xs text-muted-foreground">
                Or {formatCAD(t.annualCAD)}/mo billed annually.
              </p>
            )}
          </>
        )}
      </div>

      <ul className="space-y-2 text-sm flex-1">
        {t.features.map((f) => (
          <li key={f} className="flex gap-2">
            <span aria-hidden="true" style={{ color: 'hsl(var(--brand-teal))' }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">{t.limits}</p>

      {t.cta === 'start_trial' ? (
        <Link to="/auth/sign-up"
          className="rounded-md bg-primary px-4 py-2 text-center text-sm font-medium text-primary-foreground hover:opacity-90">
          Start {TRIAL_DAYS}-day free trial
        </Link>
      ) : (
        <a href={`mailto:${SALES_EMAIL}?subject=PharmaSync%20Enterprise%20inquiry`}
          className="rounded-md border-2 px-4 py-2 text-center text-sm font-medium hover:bg-muted/40"
          style={{ borderColor: 'hsl(var(--brand-navy))', color: 'hsl(var(--brand-navy))' }}>
          Contact sales
        </a>
      )}
    </article>
  );
}

function FAQ() {
  const items: { q: string; a: string }[] = [
    { q: 'Do I need a credit card to start the trial?', a: `No. The ${TRIAL_DAYS}-day trial is no-card.` },
    { q: 'Where is my data stored?', a: 'In Canada (ca-central-1). Details in the DPA.' },
    { q: 'How does per-location pricing work?', a: 'Each pharmacy under your organization counts as one location.' },
    { q: 'Is there a pilot program?', a: 'Yes. Founding cohort gets three months free with structured onboarding.' },
    { q: 'Can I switch plans later?', a: 'Yes. Standard ↔ Pro changes take effect at the next billing cycle.' },
    { q: 'What happens if I cancel?', a: 'Account stays active until period end, then read-only for 30 days for export.' },
  ];
  return (
    <section aria-labelledby="faq-heading" className="space-y-4">
      <h2 id="faq-heading" className="text-xl font-semibold">Frequently asked</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((it) => (
          <div key={it.q} className="rounded-lg border border-border bg-card p-4">
            <h3 className="text-sm font-medium mb-1">{it.q}</h3>
            <p className="text-sm text-muted-foreground">{it.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
